import { captureException } from '@sentry/browser'
import React, { useState } from 'react'
import XLSX from 'xlsx'
import { format, addMonths, subMonths } from 'date-fns'
import { useTranslation } from 'react-i18next'
import axios, { CancelTokenSource } from 'axios'
import { useSetRecoilState } from 'recoil'
import { loaderState } from 'recoils/atoms'

import { Button, DatePicker, DownloadProgressModal, Modal } from 'components'
import { IconDownload } from 'components/Icons'
import {
  downloadTaskReport,
  HEADER_DOWNLOAD_TICKETS,
  IParamTicket,
} from 'features/Ticket'
import { formatReport } from './TicketReportFormat'
import { useCancelToken } from 'helpers/hooks'
import { TaskForApiResponse } from './model'
import { REPORT_MAX_YEAR, REPORT_MIN_YEAR } from 'helpers/constants'

export const TicketDownloadReport: React.FC = () => {
  const [isError, setIsError] = useState<boolean>(false)
  const [percentCompleted, setPercentCompleted] = useState<number>(0)
  const [loadedSize, setLoadedSize] = useState<number>(0)
  const [totalSize, setTotalSize] = useState<number>(0)
  const [isOpenDownloadModal, setIsOpenDownloadModal] = useState<boolean>(false)
  const [isModalFilterDate, setIsModalFilterDate] = useState<boolean>(false)
  const today = new Date()
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const [startDate, setStartDate] = useState<Date>(firstDayOfMonth)
  const [endDate, setEndDate] = useState<Date>(today)

  const { t } = useTranslation('ticket')
  const { isCancel } = useCancelToken()
  const setLoader = useSetRecoilState(loaderState)

  const base = 'f-ticket-bottom-con'
  const source = axios.CancelToken.source()

  const getFileName = () => {
    return `Fixtab-Export-(${format(startDate, 'dd/MM/yyyy')} - ${format(
      endDate,
      'dd/MM/yyyy',
    )}).xlsx`
  }

  const handleStartDateChange = (newStartDate: Date) => {
    setStartDate(newStartDate)
    if (newStartDate > endDate) {
      const today = new Date()
      const maxAllowedDate = new Date(REPORT_MAX_YEAR, 11, 31)

      let potentialEndDate = addMonths(newStartDate, 1)
      const upperBoundary = today < maxAllowedDate ? today : maxAllowedDate
      if (potentialEndDate > upperBoundary) {
        potentialEndDate = upperBoundary
      }
      setEndDate(potentialEndDate)
    }
  }

  const handleEndDateChange = (newEndDate: Date) => {
    setEndDate(newEndDate)
    if (newEndDate < startDate) {
      const minAllowedDate = new Date(REPORT_MIN_YEAR, 0, 1)
      let newStartDate = subMonths(newEndDate, 1)

      if (newStartDate < minAllowedDate) {
        newStartDate = minAllowedDate
      }
      setStartDate(newStartDate)
    }
  }

  const disabledDate = (date: Date) => {
    const today = new Date()
    today.setHours(23, 59, 59, 999)
    const year = date.getFullYear()

    return date > today || year < REPORT_MIN_YEAR || year > REPORT_MAX_YEAR
  }

  const getTaskReport = (report?: TaskForApiResponse[]) => {
    const filename = getFileName()
    const workBook = XLSX.utils.book_new()
    const workSheet = XLSX.utils.aoa_to_sheet(HEADER_DOWNLOAD_TICKETS)
    if (report && report.length) {
      const newData = formatReport(report)
      XLSX.utils.sheet_add_json(workSheet, newData, {
        origin: -1,
        skipHeader: true,
      })
    }

    const workSheetCols = HEADER_DOWNLOAD_TICKETS[0].map(() => ({ wpx: 120 }))
    workSheet['!cols'] = workSheetCols

    XLSX.utils.book_append_sheet(workBook, workSheet, 'Ticket Report')
    XLSX.writeFile(workBook, filename)
    setLoader(false)
  }

  const downloadFile = async (
    source: CancelTokenSource,
    filter?: IParamTicket,
  ) => {
    try {
      const { data: tasks } = await downloadTaskReport(
        {
          cancelToken: source.token,
          onDownloadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent

            setPercentCompleted(Math.round((loaded * 100) / total))
            setLoadedSize(Math.round(loaded / 1000))
            setTotalSize(Math.round(total / 1000))
          },
        },
        {
          ...filter,
        },
      )

      getTaskReport(tasks)
      setIsError(false)
      source.cancel()
    } catch (error) {
      if (isCancel(error)) return

      captureException(error)
      setIsError(true)
    }
  }

  const handleClick = () => {
    setIsModalFilterDate(false)
    setIsOpenDownloadModal(true)
    setLoader(true)
    setPercentCompleted(0)
    setLoadedSize(0)
    setTotalSize(0)

    handleDownLoadReport()
  }

  const handleCloseDownloadModal = () => {
    setIsOpenDownloadModal(false)
  }

  const handleDownLoadReport = () => {
    const finalStartDate = new Date(
      Date.UTC(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        0,
        0,
        0,
        0,
      ),
    )
    const finalEndDate = new Date(
      Date.UTC(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate(),
        23,
        59,
        59,
        999,
      ),
    )
    downloadFile(source, {
      startDate: finalStartDate,
      endDate: finalEndDate,
    })
  }

  return (
    <div className={`${base}__download-report`}>
      <Button
        variants='outline'
        withIcon={<IconDownload />}
        onClick={() => setIsModalFilterDate(true)}
      >
        {t('exportXLSX.button')}
      </Button>

      <DownloadProgressModal
        percent={percentCompleted}
        loadedSize={loadedSize}
        totalSize={totalSize}
        fileName={getFileName()}
        isOpen={isOpenDownloadModal}
        isError={isError}
        onClose={handleCloseDownloadModal}
      />

      {isModalFilterDate && (
        <Modal
          isIcon={false}
          isOpen={isModalFilterDate}
          onClose={() => setIsModalFilterDate(false)}
        >
          <div className={`${base}__filter-date-modal`}>
            <div className={`${base}__filter-date-modal__header`}>
              <span className={`${base}__filter-date-modal__header-title`}>
                {t('modalExcelReport.title')}
              </span>
              <span className={`${base}__filter-date-modal__header-subtitle`}>
                {t('modalExcelReport.description')}
              </span>
            </div>

            <div className={`${base}__filter-date-modal__select-date`}>
              <div className={`${base}__filter-date-modal__select-date-item`}>
                <span>{t('modalExcelReport.startDate')}</span>
                <DatePicker
                  className={`${base}__filter-date-picker`}
                  value={startDate}
                  onChange={handleStartDateChange}
                  withIcon={false}
                  disabledDate={disabledDate}
                />
              </div>
              <div className={`${base}__filter-date-modal__select-date-item`}>
                <span>{t('modalExcelReport.endDate')}</span>
                <DatePicker
                  className={`${base}__filter-date-picker`}
                  value={endDate}
                  onChange={handleEndDateChange}
                  withIcon={false}
                  disabledDate={disabledDate}
                />
              </div>
            </div>

            <div className={`${base}__filter-date-modal__button-container`}>
              <Button
                variants='secondary'
                onClick={() => setIsModalFilterDate(false)}
                className={`${base}__filter-date-modal__button`}
              >
                {t('modalExcelReport.cancel')}
              </Button>
              <Button
                variants='primary'
                onClick={handleClick}
                className={`${base}__filter-date-modal__button`}
              >
                {t('modalExcelReport.export')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default TicketDownloadReport
